import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Search, X, Check } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'

interface Listino {
  id: string
  codice: string
  descrizione: string
  prezzo_unitario: number
  unita_misura: string
  attivo: boolean
  note: string | null
  created_at: string
}

interface ListinoForm {
  codice: string
  descrizione: string
  prezzo_unitario: string
  unita_misura: string
  attivo: boolean
  note: string
}

const emptyForm: ListinoForm = {
  codice: '',
  descrizione: '',
  prezzo_unitario: '0',
  unita_misura: 'pz',
  attivo: true,
  note: '',
}

const unitaMisura = [
  { value: 'pz', label: 'Pezzo (pz)' },
  { value: 'kg', label: 'Kilogrammo (kg)' },
  { value: 'g', label: 'Grammo (g)' },
  { value: 'm', label: 'Metro (m)' },
  { value: 'cm', label: 'Centimetro (cm)' },
  { value: 'mq', label: 'Metro quadro (mq)' },
  { value: 'mc', label: 'Metro cubo (mc)' },
  { value: 'lt', label: 'Litro (lt)' },
  { value: 'h', label: 'Ora (h)' },
  { value: 'gg', label: 'Giorno (gg)' },
]

export default function ListinoPage() {
  const [listino, setListino] = useState<Listino[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<Listino | null>(null)
  const [form, setForm] = useState<ListinoForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Toast e Confirm
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Listino | null>(null)

  useEffect(() => {
    loadListino()
  }, [])

  const loadListino = async () => {
    try {
      setLoading(true)
      const response = await api.get('/v1/anagrafiche/listino')
      setListino(response.data)
    } catch (err) {
      console.error('Errore caricamento listino:', err)
      setToast({ message: 'Errore nel caricamento del listino', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingItem(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (item: Listino) => {
    setEditingItem(item)
    setForm({
      codice: item.codice,
      descrizione: item.descrizione,
      prezzo_unitario: item.prezzo_unitario.toString(),
      unita_misura: item.unita_misura,
      attivo: item.attivo,
      note: item.note || '',
    })
    setError('')
    setShowModal(true)
  }

  const handleDeleteClick = (item: Listino) => {
    setConfirmDelete(item)
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return

    try {
      await api.delete(`/v1/anagrafiche/listino/${confirmDelete.id}`)
      setToast({ message: 'Voce eliminata con successo', type: 'success' })
      loadListino()
    } catch (err) {
      console.error('Errore eliminazione:', err)
      setToast({ message: 'Errore durante l\'eliminazione', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data = {
        codice: form.codice,
        descrizione: form.descrizione,
        prezzo_unitario: parseFloat(form.prezzo_unitario) || 0,
        unita_misura: form.unita_misura,
        attivo: form.attivo,
        note: form.note || null,
      }

      if (editingItem) {
        await api.put(`/v1/anagrafiche/listino/${editingItem.id}`, data)
        setToast({ message: 'Voce modificata con successo', type: 'success' })
      } else {
        await api.post('/v1/anagrafiche/listino', data)
        setToast({ message: 'Voce creata con successo', type: 'success' })
      }

      setShowModal(false)
      loadListino()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg: string }> } } }
      const detail = error.response?.data?.detail
      // Gestisci sia stringhe che array di errori Pydantic
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '))
      } else {
        setError(detail || 'Errore nel salvataggio')
      }
    } finally {
      setSaving(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const filteredListino = listino.filter(item =>
    item.codice.toLowerCase().includes(search.toLowerCase()) ||
    item.descrizione.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Voce Listino"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.codice} - ${confirmDelete?.descrizione}"?`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listino</h1>
          <p className="text-gray-500">Prezzi e prodotti dell'azienda</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuova Voce
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca nel listino..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredListino.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {search ? 'Nessuna voce trovata' : 'Nessuna voce nel listino'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prezzo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">U.M.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredListino.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {item.codice}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{item.descrizione}</span>
                    </div>
                    {item.note && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{item.note}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(item.prezzo_unitario)}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{item.unita_misura}</td>
                  <td className="px-6 py-4">
                    <span className={`flex items-center gap-1 text-sm ${
                      item.attivo ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {item.attivo ? (
                        <><Check className="w-4 h-4" /> Attivo</>
                      ) : (
                        <><X className="w-4 h-4" /> Inattivo</>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Modifica"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Modifica Voce Listino' : 'Nuova Voce Listino'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Codice *
                </label>
                <input
                  type="text"
                  required
                  value={form.codice}
                  onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                  placeholder="es. PROD001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione *
                </label>
                <input
                  type="text"
                  required
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prezzo Unitario
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.prezzo_unitario}
                      onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })}
                      className="w-full px-3 py-2 pr-8 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unità di Misura
                  </label>
                  <select
                    value={form.unita_misura}
                    onChange={(e) => setForm({ ...form, unita_misura: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {unitaMisura.map((um) => (
                      <option key={um.value} value={um.value}>
                        {um.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.attivo}
                    onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Voce attiva</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
